import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from 'jwt-decode';
import {
  FiUsers, FiCalendar, FiBriefcase, FiDollarSign,
  FiTrendingUp, FiFileText, FiBarChart2, FiSettings,
  FiPlus, FiList, FiUser, FiClock, FiCheckSquare,
  FiDownload, FiTarget, FiFile, FiFolder, FiAward,
  FiHome, FiMenu, FiX, FiChevronDown, FiChevronRight, FiChevronLeft,
  FiBell, FiSearch, FiMoreVertical, FiActivity,
  FiShoppingCart, FiLock, FiTrendingDown, FiLogOut,
  FiMail, FiPhone, FiMapPin, FiSun, FiMoon, FiMaximize2, FiEdit2
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import { FiHelpCircle } from "react-icons/fi";

function Sidebar({ currentUser = null, onNavigate, isSidebarOpen = true, isMobile = false, onToggleSidebar }) {
  const [expandedMenus, setExpandedMenus] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  // Get user permissions from token or currentUser
  const getUserPermissions = useCallback(() => {
    if (currentUser?.accessPermissions) {
      return currentUser.accessPermissions;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const decoded = jwtDecode(token);
        return decoded.accessPermissions || [];
      }
    } catch (error) {
      console.error('Failed to extract permissions from JWT:', error);
    }

    return [];
  }, [currentUser]);



  const getUserRole = useCallback(() => {
    if (currentUser?.role) {
      return currentUser.role;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const decoded = jwtDecode(token);
        return decoded.role || 'employee';
      }
    } catch (error) {
      console.error('Failed to extract role from JWT:', error);
    }

    return 'employee';
  }, [currentUser]);

  const userPermissions = getUserPermissions();
  const userRole = getUserRole();

  // Check if user has specific permission
  const hasPermission = useCallback((permission) => {
    if (userRole === 'superadmin' || userRole === 'company' || userRole === 'franchise') {
      return true; // Super admin, company, and franchise have all permissions
    }
    return userPermissions.includes(permission);
  }, [userPermissions, userRole]);

  // Check if user is super employee
  const isSuperEmployee = useCallback(() => {
    return userRole === 'super_employee';
  }, [userRole]);

  // Combined Role-Based + Permission-Based Menu Structure
  const getCombinedMenu = useMemo(() => {
    // Define all possible menu items with permissions
    const commonEmployeeManagerMenu = [
      {
        title: "Dashboard",
        icon: <FiHome className="text-xl" />,
        path: "/emp-dashboard",
        requiredPermission: null,
        submenus: []
      },
      {
        title: "Task Management",
        icon: <FiList className="text-xl" />,
        requiredPermission: 'list_task',
        submenus: [
          {
            path: "/tasks",
            title: "Create Task",
            icon: <FiCheckSquare />,
            requiredPermission: 'create_task'
          },
          {
            path: "/TaskPerformance",
            title: "Task Performance",
            icon: <FiCheckSquare />,
            requiredPermission: 'task_performance'
          },
        ]
      },
      {
        title: "Lead Management",
        icon: <FiTarget className="text-xl" />,
        requiredPermission: 'lead_management',
        submenus: [
          {
            path: "/lead-mng",
            title: "Lead Management",
            icon: <FiTarget />,
            requiredPermission: 'lead_management'
          },
        ]
      },
      {
        title: "Project",
        icon: <FiBriefcase className="text-xl" />,
        requiredPermission: 'project_dashboard',
        submenus: [
          {
            path: "/dashboard-projectmgnt",
            title: "Project Dashboard",
            icon: <FiBarChart2 />,
            requiredPermission: 'project_dashboard'
          },
          {
            path: "/projects",
            title: "Projects",
            icon: <FiFolder />,
            requiredPermission: 'projects'
          },

          {
            path: "/kanban",
            title: "Project Tracker",
            icon: <FiTarget />,
            requiredPermission: 'project_tracker'
          },
          {
            path: "/project-tasks",
            title: "Project Task",
            icon: <FiClock />,
            requiredPermission: 'project_tasks'
          },
          {
            path: "/reports",
            title: "Reports",
            icon: <FiFileText />,
            requiredPermission: 'project_reports'
          },
        ]
      },
      {
        title: "Support",
        icon: <FiHelpCircle className="text-xl" />,
        requiredPermission: 'customer_support',
        submenus: [
          {
            path: "/customer-support",
            title: "Customer Support",
            icon: <FiUsers />,
            requiredPermission: 'customer_support'
          },
          {
            path: "/sup-tickets",
            title: "Support Tickets",
            icon: <FiHelpCircle />,
            requiredPermission: 'customer_support' // same access as customer support
          },
          {
            path: "/com-tickets",
            title: "Company Tickets",
            icon: <FiUser />,
            requiredPermission: 'com_tickets'
          },
          {
            path: "/employee-performance",
            title: "Employee Performance",
            icon: <FiUser />,
            requiredPermission: 'employee_performance'
          },
        ]
      },
      {
        title: "Attendance",
        icon: <FiCalendar className="text-xl" />,
        requiredPermission: 'daily_attendace',
        submenus: [
          {
            path: "/daily-attendance",
            title: "Daily Attendance",
            icon: <FiClock />,
            requiredPermission: 'daily_attendace'
          },
          {
            path: "/manual-attendance",
            title: "Manual Attendance",
            icon: <FiEdit2 />,
            requiredPermission: 'daily_attendace'
          },
          {
            path: "/attendance-reports",
            title: "Attendance Reports",
            icon: <FiBarChart2 />,
            requiredPermission: 'attendance_report'
          },
        ]
      },
      {
        title: "Payroll",
        icon: <FaRupeeSign className="text-xl" />,
        requiredPermission: 'salary_generation',
        submenus: [
          {
            path: "/generate-salary",
            title: "Generate Salary",
            icon: <FaRupeeSign />,
            requiredPermission: 'salary_generation'
          },
        ]
      },
      {
        title: "Leave",
        icon: <FiBriefcase className="text-xl" />,
        requiredPermission: 'leave_request',
        submenus: [
          {
            path: "/leave-approval",
            title: "Leave Approval",
            icon: <FiCheckSquare />,
            requiredPermission: 'leave_approval'
          },
          {
            path: "/leave-request",
            title: "Leave Request",
            icon: <FiPlus />,
            requiredPermission: 'leave_request'
          },
        ]
      },
      {
        title: "Employee Management",
        icon: <FiUsers className="text-xl" />,
        requiredPermission: 'list_teammember',
        submenus: [
          {
            path: "/add-employee",
            title: "Add Employee",
            icon: <FiPlus />,
            requiredPermission: 'create_teammember'
          },
          {
            path: "/employee-report",
            title: "Employee Report",
            icon: <FiUsers />,
            requiredPermission: 'employee_report'
          },
        ]
      },
      {
        title: "Management",
        icon: <FiSettings className="text-xl" />,
        requiredPermission: null,
        submenus: [
          {
            path: "/create-department",
            title: "Departments",
            icon: <FiUsers />,
            requiredPermission: 'create_department'
          },
          {
            path: "/designations",
            title: "Designations",
            icon: <FiUser />,
            requiredPermission: 'update_department'
          },
          {
            path: "/shift-management",
            title: "Shift Management",
            icon: <FiCheckSquare />,
            requiredPermission: 'settings_shift'
          },

          {
            path: "/leave-type",
            title: "Leave Type",
            icon: <FiFileText />,
            requiredPermission: 'leave_type'
          },
          {
            path: "/update_leave",
            title: "Holidays",
            icon: <FiCalendar />,
            requiredPermission: 'update_leave'
          },
          {
            path: "/whatsapp-msg",
            title: "WhatsApp Msg",
            icon: <FiMail />,
            requiredPermission: null
          },
        ]
      }
    ];

    const companyMenu = [
      {
        title: "Dashboard",
        icon: <FiHome className="text-xl" />,
        path: "/comprehensivedashboard",
        requiredPermission: null,
        submenus: []
      },
      {
        title: "Task Management",
        icon: <FiList className="text-xl" />,
        requiredPermission: 'list_task',
        submenus: [
          {
            path: "/tasks",
            title: "Create Task",
            icon: <FiCheckSquare />,
            requiredPermission: 'create_task'
          },
          {
            path: "/TaskPerformance",
            title: "Task Performance",
            icon: <FiCheckSquare />,
            requiredPermission: 'task_performance'
          },
        ]
      },
      {
        title: "Lead Management",
        icon: <FiTarget className="text-xl" />,
        requiredPermission: 'lead_management',
        submenus: [
          {
            path: "/lead-mng",
            title: "Lead Management",
            icon: <FiTarget />,
            requiredPermission: 'lead_management'
          },
        ]
      },
      {
        title: "Project",
        icon: <FiBriefcase className="text-xl" />,
        requiredPermission: 'project_dashboard',
        submenus: [
          {
            path: "/dashboard-projectmgnt",
            title: "Project Dashboard",
            icon: <FiBarChart2 />,
            requiredPermission: 'project_dashboard'
          },
          {
            path: "/projects",
            title: "Projects",
            icon: <FiFolder />,
            requiredPermission: 'projects'
          },

          {
            path: "/kanban",
            title: "Project Tracker",
            icon: <FiTarget />,
            requiredPermission: 'project_tracker'
          },
          {
            path: "/project-tasks",
            title: "Project Task",
            icon: <FiClock />,
            requiredPermission: 'project_tasks'
          },
          {
            path: "/reports",
            title: "Reports",
            icon: <FiFileText />,
            requiredPermission: 'project_reports'
          },
        ]
      },
      {
        title: "Support",
        icon: <FiHelpCircle className="text-xl" />,
        requiredPermission: 'customer_support',
        submenus: [
          {
            path: "/customer-support",
            title: "Customer Support",
            icon: <FiUsers />,
            requiredPermission: 'customer_support'
          },
          {
            path: "/com-tickets",
            title: "Company Ticket",
            icon: <FiUser />,
            requiredPermission: 'com_tickets'
          },
          {
            path: "/employee-performance",
            title: "Employee Performance",
            icon: <FiUser />,
            requiredPermission: 'employee_performance'
          },
        ]
      },
      {
        title: "Attendance",
        icon: <FiCalendar className="text-xl" />,
        requiredPermission: 'daily_attendace',
        submenus: [
          {
            path: "/daily-attendance",
            title: "Daily Attendance",
            icon: <FiClock />,
            requiredPermission: 'daily_attendace'
          },
          {
            path: "/manual-attendance",
            title: "Manual Attendance",
            icon: <FiEdit2 />,
            requiredPermission: 'daily_attendace'
          },
          {
            path: "/attendance-reports",
            title: "Attendance Reports",
            icon: <FiBarChart2 />,
            requiredPermission: 'attendance_report'
          },
        ]
      },
      {
        title: "Payroll",
        icon: <FaRupeeSign className="text-xl" />,
        requiredPermission: 'salary_generation',
        submenus: [
          {
            path: "/generate-salary",
            title: "Generate Payroll",
            icon: <FaRupeeSign />,
            requiredPermission: 'salary_generation'
          },
        ]
      },
      {
        title: "Leave",
        icon: <FiBriefcase className="text-xl" />,
        requiredPermission: 'leave_approval',
        submenus: [
          {
            path: "/leave-approval",
            title: "Leave Approval",
            icon: <FiCheckSquare />,
            requiredPermission: 'leave_approval'
          },

        ]
      },
      {
        title: "Employee Management",
        icon: <FiUsers className="text-xl" />,
        requiredPermission: 'list_teammember',
        submenus: [
          {
            path: "/add-employee",
            title: "Add Employee",
            icon: <FiPlus />,
            requiredPermission: 'create_teammember'
          },
          {
            path: "/employee-report",
            title: "Employee Report",
            icon: <FiUsers />,
            requiredPermission: 'employee_report'
          }
        ]
      },
      {
        title: "Management",
        icon: <FiSettings className="text-xl" />,
        requiredPermission: null,
        submenus: [

          {
            path: "/create-department",
            title: "Departments",
            icon: <FiUsers />,
            requiredPermission: null
          },
          {
            path: "/designations",
            title: "Designations",
            icon: <FiUser />,
            requiredPermission: null
          },
          {
            path: "/shift-management",
            title: "Shift Management",
            icon: <FiCheckSquare />,
            requiredPermission: 'settings_shift'
          },
          {
            path: "/leave-type",
            title: "Leave Type",
            icon: <FiFileText />,
            requiredPermission: 'leave_type'
          },
          {
            path: "/branch",
            title: "Add Location",
            icon: <FiMapPin />,
            requiredPermission: null
          },

          {
            path: "/update_leave",
            title: "Holidays",
            icon: <FiCalendar />,
            requiredPermission: 'update_leave'
          },
          {
            path: "/whatsapp-msg",
            title: "WhatsApp Msg",
            icon: <FiMail />,
            requiredPermission: null
          },
        ]
      }
    ];

    const superAdminMenu = [
      {
        title: "Dashboard",
        icon: <FiHome className="text-xl" />,
        path: "/sup-dashboard",
        requiredPermission: null,
        submenus: []
      },


      {
        title: "Setting",
        icon: <FiSettings className="text-xl" />,
        requiredPermission: null,
        submenus: [
          {
            path: "/super-employee",
            title: "Add Employees",
            icon: <FiBriefcase />,
            requiredPermission: null
          },
          {
            path: "/companies",
            title: "Companies",
            icon: <FiBriefcase />,
            requiredPermission: null
          },
          {
            path: "/franchises",
            title: "Franchises",
            icon: <FiSettings />,
            requiredPermission: null
          },
          {
            path: "/subscription",
            title: "Subscription",
            icon: <FiSettings />,
            requiredPermission: null
          },
        ]
      }
    ];

    const franchiseMenu = [
      {
        title: "Dashboard",
        icon: <FiHome className="text-xl" />,
        path: "/sup-dashboard",
        requiredPermission: null,
        submenus: []
      },

      {
        title: "Setting",
        icon: <FiSettings className="text-xl" />,
        requiredPermission: null,
        submenus: [
          {
            path: "/companies",
            title: "Companies",
            icon: <FiBriefcase />,
            requiredPermission: null
          },
        ]
      }
    ];

    const superEmployeeMenu = [
      {
        title: "Dashboard",
        icon: <FiHome className="text-xl" />,
        path: "/sup-dashboard",
        requiredPermission: null,
        submenus: []
      },
      {
        title: "Setting",
        icon: <FiSettings className="text-xl" />,
        requiredPermission: null,
        submenus: [
          {
            path: "/super-employee",
            title: "Add Employees",
            icon: <FiBriefcase />,
            requiredPermission: 'super_employee'
          },
          {
            path: "/companies",
            title: "Companies",
            icon: <FiBriefcase />,
            requiredPermission: 'companies'
          },
          {
            path: "/franchises",
            title: "Franchises",
            icon: <FiSettings />,
            requiredPermission: 'franchises'
          },
          {
            path: "/subscription",
            title: "Subscription",
            icon: <FiSettings />,
            requiredPermission: 'subscription'
          },
        ]
      }
    ];



    // Get base menu based on role
    let baseMenu;
    switch (userRole) {
      case 'superadmin':
        baseMenu = superAdminMenu;
        break;
      case 'franchise':
        baseMenu = franchiseMenu;
        break;
      case 'company':
        // Parse subscription plan if it's a string
        let subscriptionPermissions = {};
        try {
          if (currentUser?.businessSubscriptionPlan) {
            const plan = typeof currentUser.businessSubscriptionPlan === 'string'
              ? JSON.parse(currentUser.businessSubscriptionPlan)
              : currentUser.businessSubscriptionPlan;

            subscriptionPermissions = plan.accessPermissions || {};
          }
        } catch (error) {
          console.error("Error parsing subscription plan:", error);
        }

        // Filter company menu based on subscription permissions
        baseMenu = companyMenu.filter(item => {
          // Always show Dashboard and Setting (if any)
          if (item.title === "Dashboard" || item.title === "Setting") return true;

          // Map menu titles to permission keys
          const permissionMap = {
            "Task Management": "taskManagement",
            "Lead Management": "leadManagement",
            "Project": "projectManagement",
            "Support": "support",
            "Attendance": "hrms",
            "Leave": "hrms",
            "Payroll": "hrms"
          };

          const permissionKey = permissionMap[item.title];

          // If no mapping found, show it (or maybe hide? safer to show if not restricted)
          if (!permissionKey) return true;

          return subscriptionPermissions[permissionKey] === true;
        });
        break;
      case 'super_employee':
        baseMenu = superEmployeeMenu;
        break;
      case 'employee':
      case 'manager':
        baseMenu = commonEmployeeManagerMenu;
        break;
      default:
        baseMenu = commonEmployeeManagerMenu;
    }
    // Filter menu items based on permissions
    return baseMenu.filter(category => {
      // For categories with a direct path (no submenus), check permission
      if (category.path && (!category.submenus || category.submenus.length === 0)) {
        return category.requiredPermission === null || hasPermission(category.requiredPermission);
      }

      // For categories with submenus, check if user has access to the category itself
      // OR if they have access to at least one submenu
      if (category.submenus && category.submenus.length > 0) {
        const accessibleSubmenus = category.submenus.filter(submenu =>
          submenu.requiredPermission === null || hasPermission(submenu.requiredPermission)
        );

        // Show category only if there are accessible submenus
        return accessibleSubmenus.length > 0;
      }

      return false;
    }).map(category => ({
      ...category,
      // Filter submenus based on permissions
      submenus: category.submenus.filter(submenu =>
        submenu.requiredPermission === null || hasPermission(submenu.requiredPermission)
      )
    })).filter(category => {
      // Final filter: show only if there are accessible submenus OR if it's a direct path item with permission
      if (category.path && (!category.submenus || category.submenus.length === 0)) {
        return category.requiredPermission === null || hasPermission(category.requiredPermission);
      }
      return category.submenus.length > 0;
    });
  }, [hasPermission, userRole]);

  // Toggle submenu expansion
  const toggleMenu = (menuTitle) => {
    setExpandedMenus(prev => {
      if (prev[menuTitle]) {
        return {};
      } else {
        return {
          [menuTitle]: true
        };
      }
    });
  };

  // Handle navigation
  const handleNavigation = (path, title) => {
    if (onNavigate) {
      onNavigate(path, title);
    } else {
      navigate(path);
    }
    // Auto-close sidebar on mobile after navigation
    if ((isMobile || window.innerWidth < 768) && onToggleSidebar && isSidebarOpen) {
      onToggleSidebar();
    }
  };

  // Auto-expand menu based on current route
  useEffect(() => {
    const currentMenu = getCombinedMenu.find(category =>
      category.path === location.pathname ||
      category.submenus.some(submenu => submenu.path === location.pathname)
    );

    if (currentMenu && currentMenu.submenus.length > 0) {
      setExpandedMenus(prev => ({
        ...prev,
        [currentMenu.title]: true
      }));
    }
  }, [location.pathname, getCombinedMenu]);

  useEffect(() => {
    console.log('User Role:', userRole);
    console.log('User Permissions:', userPermissions);
    console.log('Is Super Employee:', isSuperEmployee());
    console.log('Final Accessible Menu:', getCombinedMenu);
  }, [userPermissions, userRole, getCombinedMenu, isSuperEmployee]);

  return (
    <div className={`bg-[#282a42] shadow-2xl transition-all duration-300 border-r border-[#30334e] flex-shrink-0
      fixed inset-y-0 left-0 h-full z-50
      md:relative md:inset-y-auto md:left-auto md:h-screen
      ${isSidebarOpen
        ? 'w-64 translate-x-0'
        : '-translate-x-full md:translate-x-0 md:w-20'
      }`}
      style={!isSidebarOpen ? { overflow: 'visible' } : {}}
    >
      {/* Tooltip styles for collapsed sidebar */}
      <style>{`
        .sidebar-tooltip {
          visibility: hidden;
          opacity: 0;
          position: absolute;
          left: calc(100% + 12px);
          top: 50%;
          transform: translateY(-50%);
          background: #282a42;
          color: #fff;
          padding: 6px 14px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          white-space: nowrap;
          z-index: 9999;
          pointer-events: none;
          transition: opacity 0.2s ease, visibility 0.2s ease;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          border: 1px solid #3b3d57;
        }
        .sidebar-tooltip::before {
          content: '';
          position: absolute;
          right: 100%;
          top: 50%;
          transform: translateY(-50%);
          border: 6px solid transparent;
          border-right-color: #3b3d57;
        }
        .sidebar-tooltip::after {
          content: '';
          position: absolute;
          right: 100%;
          top: 50%;
          transform: translateY(-50%);
          border: 5px solid transparent;
          border-right-color: #282a42;
        }
        .sidebar-item-with-tooltip:hover .sidebar-tooltip {
          visibility: visible;
          opacity: 1;
        }

        /* ── Persistent dark scrollbar for sidebar ── */
        /* Firefox */
        .sidebar-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #5a5c7a #1e2033;
        }
        /* Chrome / Edge / Safari */
        .sidebar-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .sidebar-scrollbar::-webkit-scrollbar-track {
          background: #1e2033;
          border-radius: 4px;
        }
        .sidebar-scrollbar::-webkit-scrollbar-thumb {
          background: #5a5c7a !important;
          border-radius: 4px;
          opacity: 1 !important;
        }
        /* Keep thumb dark even when not hovering over the sidebar */
        .sidebar-scrollbar::-webkit-scrollbar-thumb:window-inactive {
          background: #5a5c7a !important;
        }
        .sidebar-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #7367F0 !important;
        }
      `}</style>
      <div className="flex items-center justify-between px-4 h-[65px] border-b border-[#30334e] flex-shrink-0">
        {isSidebarOpen && (
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-[#7367F0] rounded-lg flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-sm">H</span>
            </div>
            <h1 className="text-xl font-bold text-[#d7d8ee]">CRM</h1>
            <span className="text-xs bg-[#7367F0] text-white px-2 py-1 rounded-full capitalize">
              {userRole}
            </span>
          </div>
        )}
        <button
          onClick={onToggleSidebar}
          className="p-2 rounded-lg hover:bg-[#30334e] transition-all duration-200 text-[#d7d8ee] hover:text-white"
        >
          {isSidebarOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>

      <nav className={`mt-4 px-4 pb-4 max-h-[calc(100vh-180px)] sidebar-scrollbar ${isSidebarOpen ? 'overflow-y-auto' : 'overflow-y-auto overflow-x-visible'}`}
        style={!isSidebarOpen ? { overflow: 'visible' } : {}}
      >
        <div className="space-y-1">
          {getCombinedMenu.map((category, index) => (
            <div key={index} className="mb-1">
              {category.submenus.length > 0 ? (
                <>
                  <button
                    onClick={() => {
                      toggleMenu(category.title);
                      if (category.submenus && category.submenus.length > 0) {
                        handleNavigation(category.submenus[0].path, category.submenus[0].title);
                      }
                    }}
                    className={`flex items-center w-full p-3 transition-all duration-200 rounded-xl group relative ${!isSidebarOpen ? 'sidebar-item-with-tooltip justify-center' : ''} ${
                      category.submenus.some(s => s.path === location.pathname)
                        ? 'bg-[#7367F0] text-white shadow-lg'
                        : 'text-[#d7d8ee] hover:bg-[#30334e] hover:text-white'
                      }`}
                  >
                    <span className={`${isSidebarOpen ? 'mr-3' : ''} transition-colors duration-200 ${
                      category.submenus.some(s => s.path === location.pathname) ? 'text-white' : 'text-[#b8b9d4]'
                      }`}>
                      {category.icon}
                    </span>

                    {isSidebarOpen && (
                      <>
                        <span className="flex-1 text-left font-medium text-sm">
                          {category.title}
                        </span>
                        <span className={`text-sm transition-transform duration-200 ${expandedMenus[category.title] ? 'rotate-180' : ''
                          }`}>
                          <FiChevronDown />
                        </span>
                      </>
                    )}

                    {!isSidebarOpen && (
                      <span className="sidebar-tooltip">{category.title}</span>
                    )}

                    {category.submenus.some(s => s.path === location.pathname) && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-white rounded-r-full"></div>
                    )}
                  </button>

                  {isSidebarOpen && expandedMenus[category.title] && (
                    <div className="pl-4 mt-2 space-y-1">
                      {category.submenus.map((submenu, subIndex) => (
                        <button
                          key={subIndex}
                          onClick={() => handleNavigation(submenu.path, submenu.title)}
                          className={`flex items-center w-full p-3 transition-all duration-200 rounded-xl text-sm group ${location.pathname === submenu.path
                            ? 'bg-[#3b3d57] text-white shadow-md border-l-2 border-[#7367F0]'
                            : 'text-[#d7d8ee] hover:bg-[#30334e] hover:text-white'
                            }`}
                        >
                          <span className={`mr-3 text-sm transition-colors duration-200 ${location.pathname === submenu.path ? 'text-[#7367F0]' : 'text-[#b8b9d4]'
                            }`}>
                            {submenu.icon}
                          </span>
                          <span className="font-medium">{submenu.title}</span>

                          {location.pathname === submenu.path && (
                            <div className="ml-auto w-2 h-2 bg-[#7367F0] rounded-full"></div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <button
                  onClick={() => { handleNavigation(category.path, category.title); setExpandedMenus({}); }}
                  className={`flex items-center w-full p-3 transition-all duration-200 rounded-xl group relative ${!isSidebarOpen ? 'sidebar-item-with-tooltip justify-center' : ''} ${location.pathname === category.path
                    ? 'bg-[#7367F0] text-white shadow-lg'
                    : 'text-[#d7d8ee] hover:bg-[#30334e] hover:text-white'
                    }`}
                >
                  <span className={`${isSidebarOpen ? 'mr-3' : ''} transition-colors duration-200 ${location.pathname === category.path ? 'text-white' : 'text-[#b8b9d4]'
                    }`}>
                    {category.icon}
                  </span>

                  {isSidebarOpen && (
                    <span className="font-medium text-sm">{category.title}</span>
                  )}

                  {!isSidebarOpen && (
                    <span className="sidebar-tooltip">{category.title}</span>
                  )}

                  {location.pathname === category.path && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-white rounded-r-full"></div>
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      </nav>
      <div className="p-4 border-t border-[#30334e]">
        <button
          onClick={onToggleSidebar}
          className="w-full flex items-center justify-right p-2 rounded-lg hover:bg-[#30334e] transition-all duration-200 text-[#d7d8ee] hover:text-white"
        >
          {isSidebarOpen ? <FiChevronLeft size={20} /> : <FiChevronRight size={20} />}
        </button>
      </div>
    </div>
  );
}

export default Sidebar;

